/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Stores/PipelineOutcomeFailedRunsStore" />
import { IPipelineReportBaseState, PipelineReportBaseStore } from "Pipeline/Analytics/Common/Stores/PipelineReportBaseStore";
import { IStageMetrics } from "Pipeline/Analytics/Common/Types";
import {
    IDailyTaskFailureData,
    IFailingTaskData,
    IPipelineOutcomeTaskFailureData,
    ITopFailingTasks
} from "Pipeline/Analytics/PipelineOutcome/Common/Types";
import { PipelineOutcomeFailedRunsActions } from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Actions/PipelineOutcomeFailedRunsActions";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Resources";

export interface IPipelineOutcomeFailedRunsState extends IPipelineReportBaseState<IPipelineOutcomeTaskFailureData[]> {
    stages: IStageMetrics[];
    selectedStageFullName: string;
    filteredData: IPipelineOutcomeTaskFailureData;
}

export class PipelineOutcomeFailedRunsStore extends PipelineReportBaseStore<
    PipelineOutcomeFailedRunsActions,
    IPipelineOutcomeFailedRunsState,
    IPipelineOutcomeTaskFailureData[]
> {
    constructor(public _actions: PipelineOutcomeFailedRunsActions) {
        super(_actions);
        this.initializeListener();
    }

    public dispose(): void {
        this._actions.initializeStagesAction.removeListener(this._onInitializeStageData);
        this._actions.updateDataAction.removeListener(this._onUpdateData);
        this._actions.selectStageAction.removeListener(this._onSelectStage);
        super.dispose();
    }

    protected initializeListener(): void {
        this._actions.initializeStagesAction.addListener(this._onInitializeStageData);
        this._actions.updateDataAction.addListener(this._onUpdateData);
        this._actions.selectStageAction.addListener(this._onSelectStage);
    }

    protected _onInitializeStageData = (_data: IStageMetrics[]) => {
        if (_data) {
            this._state.stages = _data;
            if (_data.length > 1) {
                const allStages = {
                    name: Resources.AllStageName,
                    fullName: Resources.AllStageName,
                    dataCount: _data.reduce((accumulator, stage) => {
                        return accumulator + stage.dataCount;
                    }, 0)
                } as IStageMetrics;
                this._state.stages.unshift(allStages);
                this._state.selectedStageFullName = this._state.stages[0].fullName;
            }
            this.emitChanged();
        }
    };

    protected _onUpdateData = (cardData: IPipelineOutcomeTaskFailureData[]) => {
        if (cardData) {
            this._state.errorText = undefined;
            this._state.data = cardData;
            this._state.filteredData = {
                topFailingTasks: { failingTasksData: [], failedCount: 0 },
                trendChartData: []
            };
            const allStagesData = this.mergeAllStageData(cardData);
            this._state.data.unshift(allStagesData);
            this._state.filteredData = this._state.data[0];
            this.emitChanged();
        }
    };

    protected _onSelectStage = (stageFullName: string) => {
        this._state.selectedStageFullName = stageFullName;
        if (this._state.data) {
            const stageData = this._state.data.filter(data => {
                if (data.stage && data.stage.fullName === stageFullName) {
                    return data;
                }
            });
            this._state.filteredData =
                stageData && stageData.length > 0
                    ? stageData[0]
                    : {
                          topFailingTasks: { failingTasksData: [], failedCount: 0 },
                          trendChartData: []
                      };
        }
        this.emitChanged();
    };

    private mergeAllStageData(cardData: IPipelineOutcomeTaskFailureData[]): IPipelineOutcomeTaskFailureData {
        const taskFailureData: { [key: string]: IFailingTaskData } = {};
        const taskTrendDataMap: { [date: string]: IDailyTaskFailureData } = {};
        const orderedDates: string[] = [];
        const topFailingTasks: ITopFailingTasks = { failingTasksData: [], failedCount: 0 };
        cardData.forEach(stageData => {
            topFailingTasks.failedCount += stageData.topFailingTasks.failedCount;
            stageData.topFailingTasks.failingTasksData.forEach(failingTask => {
                const taskName = stageData.stage!.name + "\\" + failingTask.taskName;
                if (!taskFailureData[taskName]) {
                    taskFailureData[taskName] = {
                        taskName: taskName,
                        failedCount: 0
                    };
                }
                taskFailureData[taskName].failedCount += failingTask.failedCount;
            });
            stageData.trendChartData.forEach(trendData => {
                if (!taskTrendDataMap[trendData.date]) {
                    taskTrendDataMap[trendData.date] = {
                        date: trendData.date,
                        taskFailures: []
                    };
                    orderedDates.push(trendData.date);
                }
                trendData.taskFailures.forEach(taskFailure => {
                    taskTrendDataMap[trendData.date].taskFailures.push({
                        taskName: stageData.stage!.name + "\\" + taskFailure.taskName,
                        failedCount: taskFailure.failedCount
                    });
                });
            });
        });
        topFailingTasks.failingTasksData = Object.keys(taskFailureData)
            .map(taskData => {
                return taskFailureData[taskData];
            })
            .sort((a, b) => b.failedCount - a.failedCount);
        return {
            stage: {
                name: Resources.AllStageName,
                fullName: Resources.AllStageName,
                dataCount: topFailingTasks.failedCount
            },
            topFailingTasks: topFailingTasks,
            trendChartData: orderedDates.map(date => taskTrendDataMap[date])
        };
    }
}
